import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Building2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicPay() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("full");
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/pay/${token}`).then(({ data }) => { setInv(data); setCustom(data.balance_due); })
      .catch((e) => setErr(e.response?.data?.detail || "This payment link is invalid."));
  }, [token]);

  const pay = async () => {
    const amount = mode === "full" ? inv.balance_due : Number(custom);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    if (amount > inv.balance_due) return toast.error("Amount cannot exceed the balance due");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pay/${token}/checkout`, { amount: mode === "full" ? null : amount, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setBusy(false); }
  };

  if (err) return <Center><AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" /><h1 className="mt-3 font-head text-xl font-semibold">Link Unavailable</h1><p className="mt-1 text-slate-500">{err}</p></Center>;
  if (!inv) return <Center><Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" /></Center>;
  const paid = inv.status === "Paid" || inv.balance_due <= 0;

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-testid="public-pay-card">
        <div className="bg-slate-900 text-white px-6 py-5 flex items-center gap-3">
          <Building2 className="w-6 h-6" />
          <div><div className="font-head text-lg font-semibold">SJ Property Care</div><div className="text-xs text-slate-300">SJ Contract Solutions LLC</div></div>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div><div className="text-xs text-slate-400">Invoice</div><div className="font-semibold text-slate-900" data-testid="pay-invoice-number">{inv.invoice_number}</div></div>
            <div className="text-right"><div className="text-xs text-slate-400">Balance Due</div><div className="font-head text-xl font-bold text-slate-900" data-testid="pay-balance">{money(inv.balance_due)}</div></div>
          </div>
          <div className="text-sm text-slate-600 border-t border-b border-slate-100 py-3 space-y-1">
            <div><span className="text-slate-400">Billed To: </span>{inv.client_name}</div>
            <div><span className="text-slate-400">Service Address: </span>{inv.property_address}</div>
            <div><span className="text-slate-400">Invoice Date: </span>{inv.invoice_date} · <span className="text-slate-400">Due: </span>{inv.due_date || "—"}</div>
          </div>
          {inv.description && <p className="text-sm text-slate-600">{inv.description}</p>}
          <div className="text-sm space-y-1">
            <Line l="Labor" v={money(inv.labor)} />
            <Line l="Materials" v={money(inv.materials)} />
            {inv.additional_charges > 0 && <Line l="Additional" v={money(inv.additional_charges)} />}
            {inv.tax > 0 && <Line l="Tax" v={money(inv.tax)} />}
            {inv.discount > 0 && <Line l="Discount" v={`- ${money(inv.discount)}`} />}
            <div className="flex justify-between font-semibold pt-1 border-t"><span>Total</span><span>{money(inv.total)}</span></div>
            <Line l="Paid" v={money(inv.amount_paid)} />
          </div>

          {paid ? (
            <div className="text-center bg-green-50 border border-green-200 rounded-lg py-4 text-green-700 font-medium" data-testid="pay-paid-banner">This invoice is paid in full. Thank you!</div>
          ) : (
            <>
              {inv.allow_partial && (
                <div className="space-y-2" data-testid="pay-amount-options">
                  <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === "full"} onChange={() => setMode("full")} data-testid="pay-full" /> Pay full balance ({money(inv.balance_due)})</label>
                  <label className="flex items-center gap-2 text-sm"><input type="radio" checked={mode === "custom"} onChange={() => setMode("custom")} data-testid="pay-custom-radio" /> Pay a custom amount</label>
                  {mode === "custom" && <div><Label className="text-xs">Amount</Label><Input data-testid="pay-custom-amount" type="number" value={custom} onChange={(e) => setCustom(e.target.value)} className="mt-1" /></div>}
                </div>
              )}
              <Button data-testid="pay-now-btn" onClick={pay} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 h-11">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" /> Pay {money(mode === "full" ? inv.balance_due : custom)} Securely</>}
              </Button>
              <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1"><ShieldCheck className="w-3 h-3" /> Secure checkout by Stripe · {inv.payment_terms}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4"><div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center max-w-md">{children}</div></div>;
const Line = ({ l, v }) => <div className="flex justify-between text-slate-600"><span>{l}</span><span>{v}</span></div>;
